import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import {
  Activity,
  Users,
  Clock,
  Stethoscope,
  Maximize2,
  Minimize2,
  UserCheck,
  Timer,
  Wifi,
  WifiOff,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { buildApiUrl } from '../services/apiHelpers';

// ─── CSS injected once ───────────────────────────────────────────────────────
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Syne:wght@400;600;700;800&family=DM+Sans:wght@300;400;500;600&family=JetBrains+Mono:wght@500;700&display=swap');

  * { margin: 0; padding: 0; box-sizing: border-box; }

  :root {
    --bg: #06090f;
    --surface: #0d1117;
    --surface2: #161b22;
    --border: rgba(255,255,255,0.06);
    --border2: rgba(255,255,255,0.12);
    --teal: #2563eb;
    --teal-dim: rgba(59,130,246,0.12);
    --teal-glow: rgba(59,130,246,0.25);
    --amber: #f59e0b;
    --amber-dim: rgba(245,158,11,0.12);
    --red: #ef4444;
    --red-dim: rgba(239,68,68,0.12);
    --blue: #93c5fd;
    --text: #f0f6fc;
    --text-muted: #7d8590;
    --text-sub: #3d444d;
    --radius: 16px;
    --radius-sm: 10px;
  }

  .qdisplay {
    height: 100vh;
    width: 100%;
    background: var(--bg);
    color: var(--text);
    font-family: 'DM Sans', sans-serif;
    display: flex;
    flex-direction: column;
    overflow: hidden;
    position: relative;
  }

  /* Subtle noise texture overlay */
  .qdisplay::before {
    content: '';
    position: absolute;
    inset: 0;
    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.03'/%3E%3C/svg%3E");
    pointer-events: none;
    z-index: 0;
    opacity: 0.4;
  }

  /* ─── HEADER ─── */
  .qd-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 18px 32px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    position: relative;
    z-index: 10;
    flex-shrink: 0;
  }

  .qd-header-left {
    display: flex;
    align-items: center;
    gap: 14px;
  }

  .qd-web_logo {
    width: 42px;
    height: 42px;
    background: linear-gradient(135deg, var(--teal), #00a884);
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    box-shadow: 0 0 20px var(--teal-glow);
  }

  .qd-clinic-name {
    font-family: 'Syne', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -0.3px;
  }

  .qd-doctor-avatar { overflow: hidden; }
  .qd-doctor-avatar img { width: 100%; height: 100%; object-fit: cover; display: block; }

  .qd-header-center {
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
    display: flex;
    flex-direction: column;
    align-items: center;
  }

  .qd-title {
    font-family: 'Syne', sans-serif;
    font-size: 13px;
    font-weight: 600;
    letter-spacing: 3px;
    text-transform: uppercase;
    color: var(--teal);
  }

  .qd-subtitle {
    font-size: 11px;
    color: var(--text-muted);
    letter-spacing: 1px;
    margin-top: 2px;
  }

  .qd-header-right {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  .qd-clock {
    text-align: right;
  }

  .qd-time {
    font-family: 'JetBrains Mono', monospace;
    font-size: 26px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -1px;
    line-height: 1;
  }

  .qd-date {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 3px;
    letter-spacing: 0.5px;
  }

  .qd-icon-btn {
    width: 38px;
    height: 38px;
    background: var(--surface2);
    border: 1px solid var(--border2);
    border-radius: var(--radius-sm);
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    color: var(--text-muted);
    transition: all 0.2s;
  }
  .qd-icon-btn:hover { background: var(--border2); color: var(--text); border-color: var(--teal); }

  /* ─── STATS BAR ─── */
  .qd-stats {
    display: flex;
    align-items: stretch;
    padding: 0 32px;
    background: var(--surface);
    border-bottom: 1px solid var(--border);
    flex-shrink: 0;
    gap: 0;
  }

  .qd-stat {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 14px 28px 14px 0;
    margin-right: 28px;
    border-right: 1px solid var(--border);
  }
  .qd-stat:last-child { border-right: none; margin-right: 0; }

  .qd-stat-icon {
    width: 36px;
    height: 36px;
    border-radius: 9px;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .qd-stat-icon.green { background: var(--teal-dim); color: var(--teal); }
  .qd-stat-icon.amber { background: var(--amber-dim); color: var(--amber); }
  .qd-stat-icon.blue  { background: rgba(59,130,246,0.12); color: var(--blue); }

  .qd-stat-val {
    font-family: 'Syne', sans-serif;
    font-size: 24px;
    font-weight: 800;
    color: var(--text);
    line-height: 1;
  }

  .qd-stat-label {
    font-size: 10px;
    color: var(--text-muted);
    text-transform: uppercase;
    letter-spacing: 1.2px;
    margin-top: 3px;
  }

  .qd-stat-spacer { flex: 1; }

  .qd-live-badge {
    display: flex;
    align-items: center;
    gap: 7px;
    background: var(--teal-dim);
    border: 1px solid rgba(59,130,246,0.2);
    border-radius: 20px;
    padding: 6px 14px;
    font-size: 11px;
    font-weight: 600;
    color: var(--teal);
    letter-spacing: 1px;
    text-transform: uppercase;
    align-self: center;
  }

  .qd-live-dot {
    width: 7px;
    height: 7px;
    background: var(--teal);
    border-radius: 50%;
    animation: pulse 2s infinite;
  }

  @keyframes pulse {
    0%, 100% { opacity: 1; transform: scale(1); }
    50% { opacity: 0.5; transform: scale(0.7); }
  }

  /* ─── GRID ─── */
  .qd-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(380px, 1fr));
    gap: 20px;
    padding: 24px 32px;
    flex: 1;
    overflow-y: auto;
    position: relative;
    z-index: 1;
  }

  .qd-grid::-webkit-scrollbar { width: 4px; }
  .qd-grid::-webkit-scrollbar-track { background: transparent; }
  .qd-grid::-webkit-scrollbar-thumb { background: var(--text-sub); border-radius: 2px; }

  /* ─── DOCTOR CARD ─── */
  .qd-card {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: var(--radius);
    overflow: hidden;
    display: flex;
    flex-direction: column;
    transition: border-color 0.3s, box-shadow 0.3s;
    position: relative;
  }

  .qd-card:hover {
    border-color: var(--border2);
    box-shadow: 0 0 0 1px rgba(255,255,255,0.04), 0 8px 32px rgba(0,0,0,0.4);
  }

  .qd-card-accent {
    height: 3px;
    width: 100%;
    flex-shrink: 0;
  }

  .qd-card-header {
    padding: 20px 22px 16px;
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    border-bottom: 1px solid var(--border);
  }

  .qd-doctor-avatar {
    width: 46px;
    height: 46px;
    border-radius: 12px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 20px;
    flex-shrink: 0;
    margin-right: 14px;
  }

  .qd-doctor-details { flex: 1; }

  .qd-doctor-name {
    font-family: 'Syne', sans-serif;
    font-size: 18px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -0.2px;
    line-height: 1.2;
  }

  .qd-doctor-spec {
    font-size: 12px;
    font-weight: 500;
    margin-top: 4px;
    letter-spacing: 0.3px;
  }

  .qd-doctor-qual {
    font-size: 11px;
    color: var(--text-muted);
    margin-top: 2px;
  }

  .qd-status-badge {
    padding: 4px 10px;
    border-radius: 6px;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 1px;
    text-transform: uppercase;
    flex-shrink: 0;
  }

  /* ─── TOKEN DISPLAY ─── */
  .qd-token-area {
    padding: 22px 22px 18px;
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 0 16px;
    align-items: center;
    flex: 1;
  }

  .qd-now-serving {
    flex: 1;
  }

  .qd-section-label {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 2.5px;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 8px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  .qd-section-label::before {
    content: '';
    display: inline-block;
    width: 16px;
    height: 1px;
    background: currentColor;
  }

  .qd-token-big {
    font-family: 'JetBrains Mono', monospace;
    font-size: 72px;
    font-weight: 700;
    line-height: 1;
    letter-spacing: -2px;
  }

  .qd-token-big.empty { color: var(--text-sub); font-size: 48px; }

  .qd-next-col {
    text-align: center;
    padding: 16px;
    background: var(--surface2);
    border: 1px solid var(--border);
    border-radius: var(--radius-sm);
    min-width: 100px;
  }

  .qd-next-label {
    font-size: 9px;
    font-weight: 600;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: var(--text-muted);
    margin-bottom: 8px;
  }

  .qd-next-token {
    font-family: 'JetBrains Mono', monospace;
    font-size: 32px;
    font-weight: 700;
    color: var(--text-muted);
    line-height: 1;
  }

  .qd-next-est-wait {
    font-size: 11px;
    font-weight: 600;
    color: var(--amber);
    margin-top: 6px;
    padding: 4px 8px;
    background: var(--amber-dim);
    border-radius: 6px;
    text-align: center;
    font-family: 'JetBrains Mono', monospace;
    letter-spacing: 0.5px;
  }

  /* ─── CARD FOOTER ─── */
  .qd-card-footer {
    padding: 12px 22px;
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--surface2);
  }

  .qd-waiting-info {
    display: flex;
    align-items: center;
    gap: 7px;
    font-size: 12px;
    font-weight: 500;
  }

  .qd-progress-track {
    height: 2px;
    background: var(--border2);
    border-radius: 1px;
    width: 80px;
    overflow: hidden;
  }

  .qd-progress-fill {
    height: 100%;
    border-radius: 1px;
    transition: width 0.6s cubic-bezier(0.4,0,0.2,1);
  }

  /* ─── WAITING TIME ─── */
  .qd-wait-time {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    font-weight: 500;
    color: var(--text-muted);
    padding: 8px 12px;
    background: var(--surface2);
    border-radius: 8px;
    margin-top: 8px;
  }

  .qd-wait-time-icon {
    color: var(--amber);
  }

  .qd-wait-time-value {
    font-family: 'JetBrains Mono', monospace;
    font-weight: 600;
    color: var(--amber);
  }

  .qd-avg-consult {
    font-size: 10px;
    color: var(--text-sub);
    margin-top: 4px;
  }

  /* ─── EMPTY STATE ─── */
  .qd-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 16px;
    color: var(--text-muted);
    text-align: center;
    padding: 40px;
  }

  .qd-empty-icon {
    width: 64px;
    height: 64px;
    background: var(--surface2);
    border: 1px solid var(--border2);
    border-radius: 18px;
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--text-sub);
  }

  .qd-empty-title {
    font-family: 'Syne', sans-serif;
    font-size: 22px;
    font-weight: 700;
    color: var(--text);
  }

  .qd-empty-sub {
    font-size: 14px;
    color: var(--text-muted);
    line-height: 1.6;
    max-width: 360px;
  }

  .qd-empty-clock {
    font-family: 'JetBrains Mono', monospace;
    font-size: 48px;
    font-weight: 700;
    color: var(--text);
    letter-spacing: -2px;
    margin-top: 20px;
  }

  /* ─── FOOTER ─── */
  .qd-footer {
    padding: 10px 32px;
    border-top: 1px solid var(--border);
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: var(--surface);
    flex-shrink: 0;
  }

  .qd-footer-left {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 11px;
    color: var(--text-sub);
  }

  .qd-footer-right {
    font-size: 11px;
    color: var(--text-sub);
    font-family: 'JetBrains Mono', monospace;
  }

  .qd-loading-shimmer {
    background: linear-gradient(90deg, var(--surface2) 25%, var(--border2) 50%, var(--surface2) 75%);
    background-size: 200% 100%;
    animation: shimmer 1.5s infinite;
    border-radius: 8px;
    height: 72px;
  }

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  @keyframes fadeIn {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  .qd-card { animation: fadeIn 0.4s ease both; }

  /* ─── FULLSCREEN MODE ─── */
  .qdisplay[data-fullscreen="true"] {
    position: fixed;
    inset: 0;
    width: 100vw;
    height: 100vh;
    z-index: 99999 !important;
  }

  .qdisplay[data-fullscreen="true"] .qd-header {
    padding: 18px 32px;
  }

  .qdisplay[data-fullscreen="true"] .qd-stats {
    padding: 0 32px;
  }

  .qdisplay[data-fullscreen="true"] .qd-grid {
    padding: 24px 32px;
  }

  .qdisplay[data-fullscreen="true"] .qd-footer {
    padding: 10px 32px;
  }

  .qdisplay[data-fullscreen="true"] .qd-stat {
    padding: 14px 28px 14px 0;
    margin-right: 28px;
  }

  .qdisplay[data-fullscreen="true"] .qd-stat:first-child {
    padding-left: 0;
  }

  /* Hide all siblings and parent layout elements when in fullscreen */
  :-webkit-full-screen .qdisplay {
    width: 100vw !important;
    height: 100vh !important;
  }

  :-webkit-full-screen ~ * {
    display: none !important;
  }

  :-moz-full-screen .qdisplay {
    width: 100vw !important;
    height: 100vh !important;
  }

  :-moz-full-screen ~ * {
    display: none !important;
  }

  :fullscreen .qdisplay {
    width: 100vw !important;
    height: 100vh !important;
  }

  :fullscreen ~ * {
    display: none !important;
  }

  /* Ensure fullscreen element completely covers everything */
  ::backdrop {
    background: var(--bg) !important;
  }
`;

function injectCSS() {
  if (!document.getElementById("qd-styles")) {
    const el = document.createElement("style");
    el.id = "qd-styles";
    el.textContent = CSS;
    document.head.appendChild(el);
  }
}

// ─── STATUS CONFIG ─────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  RUNNING:     { label: "Active",       color: "#00d4aa", bg: "rgba(59,130,246,0.12)",  accent: "#00d4aa" },
  PAUSED:      { label: "Paused",       color: "#f59e0b", bg: "rgba(245,158,11,0.12)", accent: "#f59e0b" },
  NOT_STARTED: { label: "Not Started",  color: "#7d8590", bg: "rgba(125,133,144,0.12)", accent: "#3d444d" },
  ENDED:       { label: "Ended",        color: "#ef4444", bg: "rgba(239,68,68,0.12)",   accent: "#ef4444" },
};

// ─── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function QueueDisplayScreen() {
  injectCSS();

  const { auth } = useAuth();
  const clinicId = auth?.clinic?.id || null;
  const clinicCode = auth?.clinic?.code || null;
  const clinicNameFallback = auth?.clinic?.name || "Clinic";

  const [clinicData,   setClinicData]   = useState(() => ({ clinicId, clinicName: auth?.clinic?.name || "" }));
  const [sessions,     setSessions]     = useState([]);
  const [doctorPhotos, setDoctorPhotos] = useState({});
  const [queues,       setQueues]       = useState({});
  const [currentTime,  setCurrentTime]  = useState(new Date());
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [lastUpdate,   setLastUpdate]   = useState(null);
  const [isOnline,     setIsOnline]     = useState(true);

  const lastCalledRef = useRef({});
  const doctorPhotoKey = [...new Set(sessions.map((session) => session.doctorId).filter(Boolean))].join(",");

  // ── Clock ────────────────────────────────────────────────────────────────────
  useEffect(() => {
    const t = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(t);
  }, []);

  // ── Fullscreen (fixed: targets document.documentElement) ────────────────────
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().then(() => {
        setIsFullscreen(true);
        document.body.style.overflow = "hidden";
        // Hide siblings/parent layout elements
        document.body.style.position = "fixed";
        document.body.style.width = "100vw";
        document.body.style.height = "100vh";
      }).catch(() => {});
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.width = "";
        document.body.style.height = "";
      }).catch(() => {});
    }
  };

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handler);
    return () => {
      document.removeEventListener("fullscreenchange", handler);
      // Cleanup on unmount
      document.body.style.overflow = "";
      document.body.style.position = "";
      document.body.style.width = "";
      document.body.style.height = "";
    };
  }, []);

  // ── Speech ───────────────────────────────────────────────────────────────────
  const speak = (text, lang = "ta-IN") => {
    const utter = new SpeechSynthesisUtterance(text);
    const voices = speechSynthesis.getVoices();
    utter.voice = voices.find(v => v.lang.includes(lang) || v.lang.startsWith("ta"))
      || voices.find(v => v.lang.startsWith("en"))
      || voices[0];
    utter.lang = lang;
    utter.rate = 0.9;
    utter.pitch = 1.1;
    utter.volume = 1;
    speechSynthesis.speak(utter);
  };

  const announceToken = (token, doctorName) => {
    if (!token) return;
    speak(`டோக்கன் எண் ${token}, ${doctorName}`, "ta-IN");
  };

  // ── Fetch ────────────────────────────────────────────────────────────────────
  const fetchClinicName = async () => {
    try {
      if (!clinicCode) {
        setClinicData({ clinicId, clinicName: clinicNameFallback });
        return;
      }
      const res = await axios.get(buildApiUrl(`clinic/${encodeURIComponent(clinicCode)}`), {
        headers: { "X-Clinic-Id": clinicId },
      });
      const data = res.data || {};
      setClinicData({
        clinicId: data.clinicId || clinicId,
        clinicName: data.clinicName || data.name || data.displayName || data.clinic_name || clinicNameFallback,
      });
    } catch (err) {
      console.warn("Failed to fetch clinic name", err);
      setClinicData({ clinicId, clinicName: clinicNameFallback });
    }
  };

  const fetchAllData = async () => {
    try {
      if (!clinicId) return;
      const res = await axios.get(
        buildApiUrl(`doctor-booking/active-sessions/${clinicId}`),
        { headers: { "X-Clinic-Id": clinicId } }
      );
      const sessionList = Array.isArray(res.data) ? res.data : (res.data.sessions || []);
      setSessions(sessionList);
      setIsOnline(true);

      if (sessionList[0]?.clinicName) {
        setClinicData({ clinicId: sessionList[0].clinicId || clinicId, clinicName: sessionList[0].clinicName });
      } else if (res.data.clinicName) {
        setClinicData({ clinicId: res.data.clinicId || clinicId, clinicName: res.data.clinicName });
      }

      const mapLiveQueueToDisplay = (data) => {
        const waiting = Array.isArray(data?.waiting) ? data.waiting : [];

        return {
          currentToken: data?.current?.tokenNo || null,
          currentPatientName: data?.current?.patientName || null,
          nextToken: data?.next?.tokenNo || null,
          nextPatientName: data?.next?.patientName || null,
          waitingCount: waiting.length,
          waiting,
          nextPatientEstimatedWaitMinutes: data?.next?.estimatedWaitMinutes ?? data?.next?.estimatedWaitMin ?? null,
          averageConsultMinutes: data?.averageConsultMinutes ?? data?.avgConsultMinutes ?? 0,
          estimatedTotalWaitMinutes: data?.estimatedTotalWaitMinutes ?? 0,
          currentPatientWaitMinutes: data?.currentPatientWaitMinutes ?? 0,
        };
      };

      const results = await Promise.all(
        sessionList.map(async (s) => {
          const sid = s.scheduleId || (s.schedule && s.schedule.id) || s.id || null;
          if (!sid) return { scheduleId: null, data: null };
          try {
            const q = await axios.get(buildApiUrl(`appointments/live-queue?scheduleId=${encodeURIComponent(sid)}`), {
              headers: { "X-Clinic-Id": clinicId },
            });
            return { scheduleId: String(sid), data: q.data };
          } catch (err) {
            return { scheduleId: String(sid), data: null };
          }
        })
      );

      const newQueues = {};
      results.forEach(r => {
        if (!r || !r.scheduleId) return;
        newQueues[r.scheduleId] = mapLiveQueueToDisplay(r.data || {});
      });

      Object.keys(newQueues).forEach(id => {
        const newToken = newQueues[id]?.currentToken;
        if (newToken && newToken !== lastCalledRef.current[id]) {
          const s = sessionList.find(x => x.scheduleId === id);
          if (s) announceToken(newToken, s.doctorName);
          lastCalledRef.current[id] = newToken;
        }
      });

      setQueues(newQueues);
      setLastUpdate(new Date());
    } catch (err) {
      console.warn("Failed to load queue data", err);
      setIsOnline(false);
    }
  };

  useEffect(() => {
    speechSynthesis.onvoiceschanged = () => {};
    if (clinicId) fetchClinicName();
    fetchAllData();

    const interval = setInterval(fetchAllData, 5000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clinicId, clinicCode]);

  useEffect(() => {
    const doctorIds = doctorPhotoKey ? doctorPhotoKey.split(",") : [];
    if (!clinicId || doctorIds.length === 0) {
      setDoctorPhotos({});
      return undefined;
    }

    let cancelled = false;
    const photoUrls = [];
    Promise.all(doctorIds.map(async (doctorId) => {
      try {
        const response = await fetch(buildApiUrl(`doctors/${doctorId}/photo/content`), {
          headers: { "X-Clinic-Id": String(clinicId) },
          credentials: "include",
        });
        if (!response.ok) return [doctorId, ""];
        const photoUrl = URL.createObjectURL(await response.blob());
        photoUrls.push(photoUrl);
        return [doctorId, photoUrl];
      } catch {
        return [doctorId, ""];
      }
    })).then((entries) => {
      if (!cancelled) setDoctorPhotos(Object.fromEntries(entries));
      else photoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
    });

    return () => {
      cancelled = true;
      photoUrls.forEach((photoUrl) => URL.revokeObjectURL(photoUrl));
    };
  }, [clinicId, doctorPhotoKey]);

  // ── Helpers ──────────────────────────────────────────────────────────────────
  const fmtTime = (d) => d.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", second: "2-digit", hour12: true, timeZone: "Asia/Kolkata" });
  const fmtDate = (d) => d.toLocaleDateString("en-IN", { weekday: "short", day: "numeric", month: "short", year: "numeric" });

  const totalWaiting = Object.values(queues).reduce((s, q) => s + (q?.waitingCount || 0), 0);
  const inConsult    = Object.values(queues).filter(q => q?.currentToken).length;
  const totalEstimatedWait = Object.values(queues).reduce((s, q) => s + (q?.estimatedTotalWaitMinutes || 0), 0);
  const activeDoctors = new Set(sessions.map((s) => s.doctorId ?? s.doctorName)).size;

  // ── EMPTY STATE ──────────────────────────────────────────────────────────────
  if (sessions.length === 0) {
    return (
      <div className="qdisplay" data-fullscreen={isFullscreen}>
        <div className="qd-header">
          <div className="qd-header-left">
            <div className="qd-web_logo"><Stethoscope size={20} color="white" /></div>
            <span className="qd-clinic-name">{clinicData?.clinicName || "Clinic"}</span>
          </div>
          <div className="qd-header-center">
            <div className="qd-title">OPD Queue Display</div>
          </div>
          <div className="qd-header-right">
            <div className="qd-clock">
              <div className="qd-time">{fmtTime(currentTime)}</div>
              <div className="qd-date">{fmtDate(currentTime)}</div>
            </div>
          </div>
        </div>
        <div className="qd-empty">
          <div className="qd-empty-icon"><Activity size={28} /></div>
          <div className="qd-empty-title">No Active Sessions</div>
          <div className="qd-empty-sub">No doctors are currently conducting consultations. Please check back shortly.</div>
          <div className="qd-empty-clock">{fmtTime(currentTime)}</div>
        </div>
      </div>
    );
  }

  // ── MAIN RENDER ──────────────────────────────────────────────────────────────
  return (
    <div className="qdisplay" data-fullscreen={isFullscreen}>
      {/* ── Header ── */}
      <div className="qd-header">
        <div className="qd-header-left">
          <div className="qd-web_logo"><Stethoscope size={20} color="white" /></div>
          <span className="qd-clinic-name">{clinicData?.clinicName || "Clinic"}</span>
        </div>

        <div className="qd-header-center">
          <div className="qd-title">Live Consultation Queue</div>
          <div className="qd-subtitle">OPD Token Display System</div>
        </div>

        <div className="qd-header-right">
          <div className="qd-clock">
            <div className="qd-time">{fmtTime(currentTime)}</div>
            <div className="qd-date">{fmtDate(currentTime)}</div>
          </div>
          <div
            className="qd-icon-btn"
            onClick={toggleFullscreen}
            title={isFullscreen ? "Exit Fullscreen" : "Enter Fullscreen"}
          >
            {isFullscreen ? <Minimize2 size={16} /> : <Maximize2 size={16} />}
          </div>
        </div>
      </div>

      {/* ── Stats Bar ── */}
      <div className="qd-stats">
        <div className="qd-stat">
          <div className="qd-stat-icon green"><Users size={16} /></div>
          <div>
            <div className="qd-stat-val">{activeDoctors}</div>
            <div className="qd-stat-label">Active Doctors</div>
          </div>
        </div>

        <div className="qd-stat">
          <div className="qd-stat-icon amber"><Timer size={16} /></div>
          <div>
            <div className="qd-stat-val">{totalWaiting}</div>
            <div className="qd-stat-label">Patients Waiting</div>
          </div>
        </div>

        <div className="qd-stat">
          <div className="qd-stat-icon blue"><UserCheck size={16} /></div>
          <div>
            <div className="qd-stat-val">{inConsult}</div>
            <div className="qd-stat-label">In Consultation</div>
          </div>
        </div>

        {totalEstimatedWait > 0 && (
          <div className="qd-stat">
            <div className="qd-stat-icon amber"><Timer size={16} /></div>
            <div>
              <div className="qd-stat-val">{totalEstimatedWait}</div>
              <div className="qd-stat-label">Est. Wait (min)</div>
            </div>
          </div>
        )}

        <div className="qd-stat-spacer" />

        <div className="qd-live-badge" style={{ marginRight: 0 }}>
          <div className="qd-live-dot" />
          {isOnline ? "Live" : "Reconnecting..."}
        </div>
      </div>

      {/* ── Grid ── */}
      <div className="qd-grid">
        {sessions.map((session, i) => {
          const queue  = queues[session.scheduleId];
          const status = STATUS_CONFIG[session.opdStatus] || STATUS_CONFIG.NOT_STARTED;
          const hasToken = queue?.currentToken;

          return (
            <div
              className="qd-card"
              key={session.scheduleId}
              style={{ animationDelay: `${i * 0.06}s` }}
            >
              {/* Accent line */}
              <div className="qd-card-accent" style={{ background: status.accent }} />

              {/* Card Header */}
              <div className="qd-card-header">
                <div style={{ display: "flex", alignItems: "flex-start", flex: 1 }}>
                  <div
                    className="qd-doctor-avatar"
                    style={{ background: `${status.color}18`, border: `1px solid ${status.color}30` }}
                  >
                    {doctorPhotos[session.doctorId] ? (
                      <img src={doctorPhotos[session.doctorId]} alt="" />
                    ) : (
                      session.gender === "FEMALE" ? "👩‍⚕️" : "👨‍⚕️"
                    )}
                  </div>
                  <div className="qd-doctor-details">
                    <div className="qd-doctor-name">Dr. {session.doctorName}</div>
                    {session.specialization && (
                      <div className="qd-doctor-spec" style={{ color: status.color }}>
                        {session.specialization}
                      </div>
                    )}
                    {session.qualification && (
                      <div className="qd-doctor-qual">{session.qualification}</div>
                    )}
                  </div>
                </div>
                <div
                  className="qd-status-badge"
                  style={{ color: status.color, background: status.bg }}
                >
                  {status.label}
                </div>
              </div>

              {/* Token Area */}
              <div className="qd-token-area">
                <div className="qd-now-serving">
                  <div className="qd-section-label">Now Serving</div>
                  {!queue ? (
                    <div className="qd-loading-shimmer" />
                  ) : (
                    <div
                      className={`qd-token-big ${!hasToken ? "empty" : ""}`}
                      style={{ color: hasToken ? status.color : undefined }}
                    >
                      {queue.currentToken || "—"}
                    </div>
                  )}
                </div>

                {queue && (
                  <div className="qd-next-col">
                    <div className="qd-next-label">Next</div>
                    <div className="qd-next-token">
                      {queue.nextToken || "—"}
                    </div>
                    {queue.nextPatientEstimatedWaitMinutes !== null && queue.nextToken && (
                      <div className="qd-next-est-wait">
                        ~{queue.nextPatientEstimatedWaitMinutes} min
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Card Footer */}
              {queue && (
                <div className="qd-card-footer">
                  <div className="qd-waiting-info" style={{ color: queue.waitingCount > 0 ? "#f59e0b" : "#7d8590" }}>
                    <Clock size={13} />
                    {queue.waitingCount > 0
                      ? `${queue.waitingCount} patient${queue.waitingCount > 1 ? "s" : ""} waiting`
                      : "Queue clear"}
                  </div>
                  <div className="qd-progress-track">
                    <div
                      className="qd-progress-fill"
                      style={{
                        width: queue.waitingCount > 0 ? `${Math.max(10, 100 - queue.waitingCount * 8)}%` : "100%",
                        background: status.color,
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Waiting Time Display */}
              {queue && queue.averageConsultMinutes > 0 && (
                <div style={{ padding: "0 22px 16px" }}>
                  <div className="qd-wait-time">
                    <Timer size={14} className="qd-wait-time-icon" />
                    <span>Est. wait: </span>
                    <span className="qd-wait-time-value">
                      {queue.estimatedTotalWaitMinutes > 0 
                        ? `~${queue.estimatedTotalWaitMinutes} min` 
                        : queue.currentPatientWaitMinutes > 0
                          ? `${queue.currentPatientWaitMinutes} min so far`
                          : "No data"}
                    </span>
                  </div>
                  <div className="qd-avg-consult">
                    Avg consult: {queue.averageConsultMinutes} min/patient
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* ── Footer ── */}
      <div className="qd-footer">
        <div className="qd-footer-left">
          {isOnline
            ? <><Wifi size={12} /> Auto-refreshes every 5 seconds</>
            : <><WifiOff size={12} /> Connection lost — retrying...</>
          }
        </div>
        <div className="qd-footer-right">
          {lastUpdate ? `Updated ${lastUpdate.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Kolkata" })}` : "—"}
        </div>
      </div>
    </div>
  );
}
